import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Eye, EyeOff, Loader2, Users } from "lucide-react";
import { acceptInvite, apiError, register } from "../../lib/auth";
import { useSession, usePlatformStatus } from "../../lib/session";
import api from "../../lib/api";
import { Wordmark } from "../../marketing/Brand";

const field =
  "w-full h-11 rounded-md bg-ac-bg border border-ac-border px-3 text-[15px] text-ac-text placeholder:text-ac-muted/70 focus:outline-none focus:border-ac-link transition-colors";

const Shell = ({ title, sub, children }) => (
  <div className="relative min-h-screen bg-ac-bg text-ac-text flex flex-col overflow-hidden">
    <header className="relative z-10 mx-auto w-full max-w-7xl px-5 sm:px-8 h-20 flex items-center">
      <Link to="/"><Wordmark /></Link>
    </header>
    <main className="relative z-10 flex-1 flex items-center justify-center px-5 pb-16">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="w-full max-w-md rounded-[10px] border border-ac-border bg-ac-surface p-8 sm:p-10">
        <h1 className="font-display text-[30px] font-semibold text-ac-text">{title}</h1>
        {sub && <p className="mt-2 text-sm text-slate-400">{sub}</p>}
        <div className="mt-8">{children}</div>
      </motion.div>
    </main>
  </div>
);

const PasswordField = ({ value, onChange, autoComplete = "new-password" }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input type={show ? "text" : "password"} required minLength={10} value={value} onChange={onChange}
        autoComplete={autoComplete} placeholder="At least 10 characters" className={`${field} pr-11`} />
      <button type="button" onClick={() => setShow(!show)} aria-label={show ? "Hide password" : "Show password"}
        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200">
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
};

const Submit = ({ busy, children }) => (
  <button type="submit" disabled={busy}
    className="w-full h-11 rounded-md bg-ac-action text-white text-[15px] font-medium hover:bg-ac-action-hover disabled:opacity-70 inline-flex items-center justify-center gap-2 transition-colors">
    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{children} <ArrowRight className="w-4 h-4" /></>}
  </button>
);

const Err = ({ children }) =>
  children ? <p className="text-sm text-rose-300 bg-rose-950/30 border border-rose-900/40 rounded-lg px-3 py-2">{children}</p> : null;

export function SignupPage() {
  const platform = usePlatformStatus();
  const { refresh } = useSession();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", workspace_name: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (platform && (platform.maintenance || !platform.signups_open)) {
    return (
      <Shell title="Sign-ups aren't open yet" sub="We're letting people in in waves. Join the waitlist and you'll be first to know.">
        <Link to="/#waitlist" className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-300 hover:text-indigo-200">
          Join the waitlist <ArrowRight className="w-4 h-4" />
        </Link>
      </Shell>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await register(form);
      await refresh();
      navigate("/app?welcome=1");
    } catch (err) {
      setError(apiError(err, "Could not create the account."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title="Create your workspace" sub="Free to start. No card needed.">
      <form onSubmit={submit} className="space-y-4">
        <input required placeholder="Your name" value={form.name} onChange={set("name")} className={field} autoComplete="name" />
        <input required type="email" placeholder="Work email" value={form.email} onChange={set("email")} className={field} autoComplete="email" />
        <PasswordField value={form.password} onChange={set("password")} />
        <input placeholder="Company or brand (optional)" value={form.workspace_name} onChange={set("workspace_name")} className={field} />
        <Err>{error}</Err>
        <Submit busy={busy}>Create workspace</Submit>
        <p className="text-xs text-slate-500 text-center leading-relaxed">
          By continuing you agree to the <Link to="/terms" className="underline hover:text-slate-300">Terms</Link> and{" "}
          <Link to="/privacy" className="underline hover:text-slate-300">Privacy Policy</Link>.
        </p>
      </form>
      <p className="mt-8 text-center text-sm text-slate-400">
        Already have an account? <Link to="/login" className="font-semibold text-indigo-300 hover:text-indigo-200">Sign in</Link>
      </p>
    </Shell>
  );
}

export function InvitePage() {
  const { token } = useParams();
  const { refresh } = useSession();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    api.get(`/auth/invite/${token}`)
      .then(({ data }) => {
        setInvite(data);
        if (data.email) setForm((f) => ({ ...f, email: data.email }));
      })
      .catch((err) => setLoadError(apiError(err, "This invite has expired or was already used.")));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await acceptInvite({ token, ...form });
      await refresh();
      navigate("/app");
    } catch (err) {
      setError(apiError(err, "Could not join the workspace."));
    } finally {
      setBusy(false);
    }
  };

  if (loadError) {
    return <Shell title="Invite unavailable" sub={loadError}><Link to="/" className="text-sm text-indigo-300">Back to the site</Link></Shell>;
  }
  if (!invite) {
    return <Shell title="Checking your invite…"><Loader2 className="w-5 h-5 animate-spin text-indigo-300" /></Shell>;
  }
  return (
    <Shell title={`Join ${invite.workspace}`} sub={`You've been invited as ${invite.role === "admin" ? "an admin" : "an editor"}.`}>
      <div className="mb-6 flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
        <Users className="w-4 h-4 text-indigo-300" />
        <span className="text-sm text-slate-300">Already have an account? Use its email and password below to add this workspace to it.</span>
      </div>
      <form onSubmit={submit} className="space-y-4">
        <input placeholder="Your name" value={form.name} onChange={set("name")} className={field} autoComplete="name" />
        <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={field}
          readOnly={Boolean(invite.email)} autoComplete="email" />
        <PasswordField value={form.password} onChange={set("password")} autoComplete="current-password" />
        <Err>{error}</Err>
        <Submit busy={busy}>Join workspace</Submit>
      </form>
    </Shell>
  );
}
